import { createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import { idx, monthAt } from "@/lib/months";

/**
 * Where a chart is drawn. The node-page preview is live (drag, commit on release) unless the
 * preview says it is read-only; the full-screen editor adds its own extras (zoom y, downstream readout).
 */
export interface ChartSurface {
	surface: "plain" | "preview" | "editor";
	/** Preview only: whether the chart can be edited in place. Overrides the chart's own `readOnly`. */
	readOnly?: boolean;
	/** Editor only: one-line "what this changed downstream" readout, drawn under the chart. */
	readout?: React.ReactNode;
}
export const ChartSurfaceContext = createContext<ChartSurface>({ surface: "plain" });
export const useChartSurface = () => useContext(ChartSurfaceContext);

export function useWidth<T extends HTMLElement>() {
	const ref = useRef<T>(null);
	const [w, setW] = useState(640);
	useLayoutEffect(() => {
		if (!ref.current) return;
		const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.floor(e.contentRect.width))));
		ro.observe(ref.current);
		return () => ro.disconnect();
	}, []);
	return [ref, w] as const;
}

export function niceTicks(lo: number, hi: number, count = 5): number[] {
	if (hi <= lo) hi = lo + 1;
	const span = hi - lo;
	const step0 = span / count;
	const mag = Math.pow(10, Math.floor(Math.log10(step0)));
	const norm = step0 / mag;
	const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
	const out: number[] = [];
	for (let v = Math.ceil(lo / step) * step; v <= hi + step * 1e-6; v += step) out.push(Number(v.toPrecision(12)));
	return out;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Time-axis ticks for grid indices [t0, t1]: each January for long views; months
 * (every 1, 2 or 3) for views of two years or less, with the year on January and the first tick.
 */
export function yearTicks(t0: number, t1: number, width?: number): { t: number; label: string }[] {
	const all = yearTicksRaw(t0, t1);
	if (!width) return all;
	// Thin labels so they never collide: about 64 px per month label, 40 px per year.
	const per = all.some((a) => a.label.length > 4) ? 64 : 40;
	const max = Math.max(2, Math.floor(width / per));
	if (all.length <= max) return all;
	const step = Math.ceil(all.length / max);
	return all.filter((_, i) => i % step === 0);
}

function yearTicksRaw(t0: number, t1: number): { t: number; label: string }[] {
	const span = t1 - t0;
	if (span <= 24) {
		const step = span <= 8 ? 1 : span <= 15 ? 2 : 3;
		const out: { t: number; label: string }[] = [];
		for (let t = Math.ceil(t0); t <= t1; t++) {
			const m = monthAt(t);
			const mm = Number(m.slice(5));
			if ((mm - 1) % step !== 0) continue;
			const withYear = !out.length || mm === 1;
			out.push({ t, label: withYear ? `${MONTHS[mm - 1]} ${m.slice(0, 4)}` : MONTHS[mm - 1] });
		}
		return out;
	}
	const out: { t: number; label: string }[] = [];
	for (let t = Math.ceil(t0); t <= t1; t++) {
		const m = monthAt(t);
		if (m.endsWith("-01")) out.push({ t, label: m.slice(0, 4) });
	}
	const step = out.length > 7 ? 2 : 1;
	return out.filter((_, i) => i % step === 0);
}

export const PAD = { l: 52, r: 18, t: 18, b: 30 };

export function linePath(pts: [number, number][]): string {
	return pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
}

export const VIEW_ENDS = [
	{ label: "to 2027", t: idx("2027-12") },
	{ label: "to 2030", t: idx("2030-12") },
	{ label: "to 2035", t: idx("2035-12") },
];

export type Zoom = "month" | "quarter" | "year";

/** One control for range and period size (PRD §9): each range has the period size that keeps the bars readable. */
export const CHART_VIEWS: { t: number; year: string; zoom: Zoom; zoomLabel: string }[] = [
	{ t: idx("2027-12"), year: "2027", zoom: "month", zoomLabel: "months" },
	{ t: idx("2030-12"), year: "2030", zoom: "quarter", zoomLabel: "quarters" },
	{ t: idx("2035-12"), year: "2035", zoom: "year", zoomLabel: "years" },
];

/** Period size that goes with a view end. */
export function zoomFor(viewEnd: number): Zoom {
	return (CHART_VIEWS.find((v) => v.t >= viewEnd) ?? CHART_VIEWS[CHART_VIEWS.length - 1]).zoom;
}

/**
 * Default view end for an event: 2027 (by month) when a market ladder is present, since its
 * rungs are near-term; otherwise far enough to include the last stated point.
 */
export function defaultEventViewEnd(by: Record<string, number>, hasMarket: boolean): number {
	if (hasMarket) return CHART_VIEWS[0].t;
	const last = Object.keys(by).sort().at(-1);
	return last && idx(last) > CHART_VIEWS[1].t ? CHART_VIEWS[2].t : CHART_VIEWS[1].t;
}
